import React, { useState, useEffect, useRef } from 'react';
import {
  FiPlus, FiEdit2, FiPower, FiUpload, FiX,
  FiCheckCircle, FiAlertCircle, FiGrid,
  FiPhone, FiMail, FiMapPin, FiHash,
  FiUser, FiLock, FiEye, FiEyeOff, FiShield
} from 'react-icons/fi';
import { getBusinesses, createBusiness, updateBusiness, toggleBusinessStatus } from '../services/api';
import '../styles/EmpresasPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const EMPTY_FORM = {
  name: '', description: '', business_type: '', ruc: '',
  phone: '', email: '', address: '', city: '', country: 'Peru', currency: 'PEN',
};

const EMPTY_ADMIN = {
  admin_full_name: '', admin_username: '', admin_email: '', admin_password: '',
};

function EmpresasPage() {
  const [businesses, setBusinesses]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [modalOpen, setModalOpen]       = useState(false);
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [logoFile, setLogoFile]         = useState(null);
  const [logoPreview, setLogoPreview]   = useState(null);
  const [saving, setSaving]             = useState(false);
  const [saveError, setSaveError]       = useState('');
  const [confirmSuspend, setConfirmSuspend] = useState(null);

  // Admin user section
  const [createAdmin, setCreateAdmin]   = useState(true);
  const [adminForm, setAdminForm]       = useState(EMPTY_ADMIN);
  const [showAdminPass, setShowAdminPass] = useState(false);

  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getBusinesses();
      setBusinesses(res.data.data || []);
    } catch {
      setError('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setLogoPreview(null);
    setSaveError('');
    setCreateAdmin(true);
    setAdminForm(EMPTY_ADMIN);
    setShowAdminPass(false);
    setModalOpen(true);
  };

  const openEdit = (biz) => {
    setEditing(biz);
    setForm({
      name: biz.name || '', description: biz.description || '',
      business_type: biz.business_type || '', ruc: biz.ruc || '',
      phone: biz.phone || '', email: biz.email || '',
      address: biz.address || '', city: biz.city || '',
      country: biz.country || 'Peru', currency: biz.currency || 'PEN',
    });
    setLogoFile(null);
    setLogoPreview(biz.logo_url ? `${API_URL}${biz.logo_url}` : null);
    setSaveError('');
    setModalOpen(true);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleFormChange = (e) =>
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleAdminChange = (e) =>
    setAdminForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setSaveError('El nombre de la empresa es requerido'); return; }

    // Validar admin si aplica (solo en creación)
    if (!editing && createAdmin) {
      if (!adminForm.admin_username.trim()) {
        setSaveError('El nombre de usuario del administrador es requerido'); return;
      }
      if (!adminForm.admin_password || adminForm.admin_password.length < 6) {
        setSaveError('La contraseña del administrador debe tener al menos 6 caracteres'); return;
      }
    }

    setSaving(true);
    setSaveError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (logoFile) fd.append('logo', logoFile);

      // Incluir datos de admin solo en creación
      if (!editing && createAdmin) {
        Object.entries(adminForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      }

      if (editing) {
        await updateBusiness(editing.id, fd);
      } else {
        await createBusiness(fd);
      }
      setModalOpen(false);
      load();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (biz) => {
    try {
      await toggleBusinessStatus(biz.id);
      setConfirmSuspend(null);
      load();
    } catch { /* ignore */ }
  };

  const activeCount   = businesses.filter(b => b.active).length;
  const inactiveCount = businesses.filter(b => !b.active).length;

  return (
    <div className="empresas-page">

      {/* ── Cabecera ──────────────────────────────────────────── */}
      <div className="empresas-header">
        <div>
          <h1 className="empresas-title">Gestión de Empresas</h1>
          <p className="empresas-subtitle">Panel exclusivo Superadmin — registra, edita y suspende empresas</p>
        </div>
        <button className="btn-new-empresa" onClick={openCreate}>
          <FiPlus /> Nueva Empresa
        </button>
      </div>

      {/* ── KPIs ──────────────────────────────────────────────── */}
      <div className="empresas-kpis">
        <div className="kpi-card">
          <span className="kpi-label">Total Empresas</span>
          <span className="kpi-value">{businesses.length}</span>
        </div>
        <div className="kpi-card kpi-active">
          <span className="kpi-label">Activas</span>
          <span className="kpi-value">{activeCount}</span>
        </div>
        <div className="kpi-card kpi-inactive">
          <span className="kpi-label">Suspendidas</span>
          <span className="kpi-value">{inactiveCount}</span>
        </div>
      </div>

      {loading && <div className="empresas-loading">Cargando empresas...</div>}
      {error   && <div className="empresas-error">{error}</div>}

      {/* ── Grid de empresas ──────────────────────────────────── */}
      {!loading && !error && (
        <div className="empresas-grid">
          {businesses.length === 0 && (
            <div className="empresas-empty">No hay empresas registradas. Crea la primera.</div>
          )}
          {businesses.map(biz => (
            <div key={biz.id} className={`empresa-card ${!biz.active ? 'suspended' : ''}`}>
              <div className="empresa-logo-wrap">
                {biz.logo_url ? (
                  <img src={`${API_URL}${biz.logo_url}`} alt={biz.name} className="empresa-logo" />
                ) : (
                  <div className="empresa-logo-placeholder"><FiGrid size={32} /></div>
                )}
                <span className={`empresa-status-badge ${biz.active ? 'active' : 'inactive'}`}>
                  {biz.active ? 'Activa' : 'Suspendida'}
                </span>
              </div>
              <div className="empresa-info">
                <h3 className="empresa-name">{biz.name}</h3>
                <p className="empresa-type">{biz.business_type || '—'}</p>
                {biz.ruc   && <p className="empresa-field"><FiHash  size={11} /> RUC: {biz.ruc}</p>}
                {biz.phone && <p className="empresa-field"><FiPhone size={11} /> {biz.phone}</p>}
                {biz.email && <p className="empresa-field"><FiMail  size={11} /> {biz.email}</p>}
                {biz.city  && <p className="empresa-field"><FiMapPin size={11} /> {biz.city}{biz.country ? `, ${biz.country}` : ''}</p>}
              </div>
              <div className="empresa-actions">
                <button className="btn-edit-empresa" onClick={() => openEdit(biz)}>
                  <FiEdit2 size={14} /> Editar
                </button>
                <button
                  className={`btn-toggle-empresa ${biz.active ? 'btn-suspend' : 'btn-activate'}`}
                  onClick={() => setConfirmSuspend(biz)}
                >
                  <FiPower size={14} /> {biz.active ? 'Suspender' : 'Activar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Crear / Editar ───────────────────────────────── */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="empresa-modal" onClick={e => e.stopPropagation()}>

            <div className="empresa-modal-header">
              <h2>{editing ? 'Editar Empresa' : 'Nueva Empresa'}</h2>
              <button className="btn-close-modal" onClick={() => setModalOpen(false)}><FiX /></button>
            </div>

            <div className="empresa-modal-body">

              {/* ─ Logo ─ */}
              <div className="logo-upload-section">
                <div className="logo-preview-box" onClick={() => fileRef.current?.click()}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="logo preview" className="logo-preview-img" />
                  ) : (
                    <div className="logo-upload-placeholder">
                      <FiUpload size={24} />
                      <span>Subir Logo</span>
                      <small>JPG, PNG, SVG — máx. 5MB</small>
                    </div>
                  )}
                </div>
                <input ref={fileRef} type="file" accept="image/*"
                  className="hidden-file-input" onChange={handleLogoChange} />
                {logoPreview && (
                  <button className="btn-remove-logo"
                    onClick={() => { setLogoFile(null); setLogoPreview(null); }}>
                    <FiX size={12} /> Quitar logo
                  </button>
                )}
              </div>

              {/* ─ Datos de la empresa ─ */}
              <div className="empresa-form-grid">
                <div className="form-group fg-full">
                  <label>Nombre de la Empresa *</label>
                  <input name="name" value={form.name} onChange={handleFormChange} placeholder="Ej: PIVASAPOLLOS" />
                </div>
                <div className="form-group">
                  <label>Tipo de Empresa</label>
                  <input name="business_type" value={form.business_type} onChange={handleFormChange} placeholder="Ej: Retail - Pollería" />
                </div>
                <div className="form-group">
                  <label>RUC</label>
                  <input name="ruc" value={form.ruc} onChange={handleFormChange} placeholder="20100000001" maxLength={11} />
                </div>
                <div className="form-group">
                  <label>Teléfono</label>
                  <input name="phone" value={form.phone} onChange={handleFormChange} placeholder="+51 945 123 456" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input name="email" type="email" value={form.email} onChange={handleFormChange} placeholder="info@empresa.com" />
                </div>
                <div className="form-group">
                  <label>Ciudad</label>
                  <input name="city" value={form.city} onChange={handleFormChange} placeholder="Lima" />
                </div>
                <div className="form-group">
                  <label>País</label>
                  <input name="country" value={form.country} onChange={handleFormChange} placeholder="Peru" />
                </div>
                <div className="form-group">
                  <label>Moneda</label>
                  <select name="currency" value={form.currency} onChange={handleFormChange}>
                    <option value="PEN">PEN — Soles</option>
                    <option value="USD">USD — Dólares</option>
                    <option value="EUR">EUR — Euros</option>
                  </select>
                </div>
                <div className="form-group fg-full">
                  <label>Dirección</label>
                  <input name="address" value={form.address} onChange={handleFormChange} placeholder="Av. Benavides 6600, Miraflores" />
                </div>
                <div className="form-group fg-full">
                  <label>Descripción</label>
                  <textarea name="description" value={form.description} onChange={handleFormChange}
                    rows={2} placeholder="Descripción breve de la empresa..." />
                </div>
              </div>

              {/* ─ Sección Usuario Admin (solo en creación) ─ */}
              {!editing && (
                <div className="admin-section">
                  <div className="admin-section-toggle"
                    onClick={() => setCreateAdmin(v => !v)}>
                    <div className="admin-toggle-left">
                      <div className={`admin-toggle-checkbox ${createAdmin ? 'checked' : ''}`}>
                        {createAdmin && <FiCheckCircle size={13} />}
                      </div>
                      <div className="admin-toggle-info">
                        <span className="admin-toggle-title">
                          <FiShield size={13} /> Crear usuario administrador
                        </span>
                        <span className="admin-toggle-sub">
                          La empresa podrá iniciar sesión de inmediato
                        </span>
                      </div>
                    </div>
                    <span className={`admin-badge ${createAdmin ? 'admin-badge-on' : 'admin-badge-off'}`}>
                      {createAdmin ? 'Activado' : 'Omitir'}
                    </span>
                  </div>

                  {createAdmin && (
                    <div className="admin-fields">

                      {/* Nota informativa */}
                      {form.ruc && (
                        <div className="admin-login-hint">
                          <strong>Credenciales de acceso:</strong>&nbsp;
                          RUC <code>{form.ruc}</code> + usuario + contraseña
                        </div>
                      )}

                      <div className="empresa-form-grid">
                        <div className="form-group">
                          <label><FiUser size={11} /> Nombre Completo</label>
                          <input
                            name="admin_full_name"
                            value={adminForm.admin_full_name}
                            onChange={handleAdminChange}
                            placeholder="Ej: Juan Pérez"
                          />
                        </div>
                        <div className="form-group">
                          <label><FiUser size={11} /> Usuario *</label>
                          <input
                            name="admin_username"
                            value={adminForm.admin_username}
                            onChange={handleAdminChange}
                            placeholder="Ej: admin"
                            autoComplete="off"
                          />
                        </div>
                        <div className="form-group">
                          <label><FiMail size={11} /> Email (opcional)</label>
                          <input
                            name="admin_email"
                            type="email"
                            value={adminForm.admin_email}
                            onChange={handleAdminChange}
                            placeholder="admin@empresa.com"
                          />
                        </div>
                        <div className="form-group">
                          <label><FiLock size={11} /> Contraseña *</label>
                          <div className="pass-field-wrap">
                            <input
                              name="admin_password"
                              type={showAdminPass ? 'text' : 'password'}
                              value={adminForm.admin_password}
                              onChange={handleAdminChange}
                              placeholder="Mínimo 6 caracteres"
                              autoComplete="new-password"
                            />
                            <button type="button" className="pass-toggle-btn"
                              onClick={() => setShowAdminPass(v => !v)}>
                              {showAdminPass ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {saveError && (
                <div className="empresa-save-error"><FiAlertCircle /> {saveError}</div>
              )}
            </div>

            <div className="empresa-modal-footer">
              <button className="btn-cancel-modal" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-save-empresa" onClick={handleSave} disabled={saving}>
                {saving
                  ? 'Guardando...'
                  : <><FiCheckCircle /> {editing ? 'Guardar Cambios' : 'Crear Empresa'}</>
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirmar suspender/activar ────────────────────────── */}
      {confirmSuspend && (
        <div className="modal-overlay" onClick={() => setConfirmSuspend(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon">
              {confirmSuspend.active ? <FiPower size={32} /> : <FiCheckCircle size={32} />}
            </div>
            <h3>{confirmSuspend.active ? 'Suspender empresa' : 'Activar empresa'}</h3>
            <p>
              {confirmSuspend.active
                ? `¿Deseas suspender "${confirmSuspend.name}"? Los usuarios no podrán iniciar sesión.`
                : `¿Deseas activar "${confirmSuspend.name}"?`}
            </p>
            <div className="confirm-actions">
              <button className="btn-cancel-modal" onClick={() => setConfirmSuspend(null)}>Cancelar</button>
              <button
                className={`btn-confirm ${confirmSuspend.active ? 'btn-confirm-danger' : 'btn-confirm-ok'}`}
                onClick={() => handleToggle(confirmSuspend)}
              >
                {confirmSuspend.active ? 'Suspender' : 'Activar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmpresasPage;
